import React, {FC} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AvatarImage} from '../../avatars/avatarImage';
import {Skeleton} from '../../skeleton';
import {CellButton} from '../helpers/cellButton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellPropsWithAvatar, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

export interface AvatarCellModel {
  avatarIcon?: IconName;
  avatarUrl?: string;
  name: string;
}

interface AvatarCellProps extends TableCellPropsWithAvatar<AvatarCellModel> {
  onSelect?: ClickEventHandler;
}

/*
 * Styles.
 */

interface StyledAvatarSkeletonProps {
  $avatarSize: number;
}
const StyledAvatarSkeleton = styled(Skeleton)<StyledAvatarSkeletonProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  margin-right: 8px;
`;

const StyledAvatarCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

const StyledAvatarContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  overflow: hidden;
  align-items: center;
  flex: 1;
`;

interface StyledAvatarNameDivProps {
  $isSelected: boolean;
}
const StyledAvatarNameDiv = styled.div<StyledAvatarNameDivProps>`
  line-height: 16px;

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

const StyledAvatarNameInButtonDiv = styled.div<StyledAvatarNameDivProps>`
  line-height: 16px;
  padding-right: 12px;

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected, p.theme.palette.blue.shade40)}
`;

const StyledAvatarIconDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface StyledAvatarDivProps {
  $avatarSize: number;
}
const StyledAvatarDiv = styled.div<StyledAvatarDivProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  height: ${(p) => p.$avatarSize}px;
  position: relative;
  margin-right: 8px;
`;

/*
 * Component.
 */

export const AvatarCell: FC<AvatarCellProps> = (props) => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    avatarSize,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    onSelect,
  } = props;

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);

  return (
    <TableCell<AvatarCellModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledAvatarCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledAvatarContainerDiv>
            <StyledAvatarSkeleton
              $avatarSize={avatarSizePx}
              height={`${avatarSizePx}px`}
              width={`${avatarSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
            <Skeleton height="16px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledAvatarContainerDiv>
        </StyledAvatarCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledAvatarCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          {renderButtonOrLabel(value, avatarSizePx, isSelected, onSelect)}
        </StyledAvatarCellContainerDiv>
      )}
    />
  );
};

/*
 * Helpers.
 */

const renderButtonOrLabel = (
  value: AvatarCellModel,
  avatarSizePx: number,
  isSelected?: boolean,
  onSelect?: ClickEventHandler,
) => {
  if (!onSelect) {
    return (
      <StyledAvatarContainerDiv>
        <StyledAvatarDiv $avatarSize={avatarSizePx}>
          {renderAvatarImageOrIcon(value.avatarUrl, value.avatarIcon, avatarSizePx)}
        </StyledAvatarDiv>
        <StyledAvatarNameDiv $isSelected={Boolean(isSelected)}>{value.name}</StyledAvatarNameDiv>
      </StyledAvatarContainerDiv>
    );
  }
  return (
    <CellButton
      onClick={(event) => {
        if (onSelect && !event.defaultPrevented) {
          onSelect(event);
        }
      }}
      avatar={
        <StyledAvatarDiv $avatarSize={avatarSizePx}>
          {renderAvatarImageOrIcon(value.avatarUrl, value.avatarIcon, avatarSizePx)}
        </StyledAvatarDiv>
      }
      avatarSizePx={avatarSizePx}
    >
      <StyledAvatarNameInButtonDiv $isSelected={Boolean(isSelected)}>
        {value.name}
      </StyledAvatarNameInButtonDiv>
    </CellButton>
  );
};

const renderAvatarImageOrIcon = (
  avatarUrl: string | undefined,
  avatarIcon: IconName | undefined,
  avatarSizePx: number,
) => {
  if (avatarIcon && !avatarUrl) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledAvatarIconDiv>
            <Icon name={avatarIcon} size={avatarSizePx} color={theme.greys.shade60} />
          </StyledAvatarIconDiv>
        )}
      </UserThemeConsumer>
    );
  }
  if (avatarUrl) {
    return <AvatarImage imageUrl={avatarUrl} />;
  }
  return null;
};
